if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.initThumbnailClickHandlers();
        this.initNavButtons();
      }

      initNavButtons() {
        const prevBtn = this.querySelector('.gallery-nav-btn--prev');
        const nextBtn = this.querySelector('.gallery-nav-btn--next');
        if (prevBtn) {
          prevBtn.addEventListener('click', () => this.navigateGallery(-1));
        }
        if (nextBtn) {
          nextBtn.addEventListener('click', () => this.navigateGallery(1));
        }
      }

      navigateGallery(direction) {
        const galleryMain = this.querySelector('[data-gallery-main]');
        if (!galleryMain) return;
        const items = galleryMain.querySelectorAll('.gallery-main__item');
        if (items.length <= 1) return;

        let currentIndex = 0;
        items.forEach((item, i) => { if (item.classList.contains('active')) currentIndex = i; });

        let newIndex = currentIndex + direction;
        if (newIndex < 0) newIndex = items.length - 1;
        if (newIndex >= items.length) newIndex = 0;

        // Videos play inline in the main gallery (no separate modal), so they
        // are regular slides — navigate to them directly. The previous code
        // skipped any `product-media-container--video` slide, which dropped
        // every metafield video (and any inline native video) from navigation.
        const mediaId = items[newIndex].getAttribute('data-media-id');
        if (mediaId) this.setActiveMedia(mediaId);
      }

      initThumbnailClickHandlers() {
        const thumbnails = this.querySelectorAll('.gallery-thumbnails__item');
        thumbnails.forEach((thumb) => {
          thumb.addEventListener('click', () => {
            const mediaId = thumb.getAttribute('data-media-id');
            const isVideo = thumb.querySelector('.gallery-thumbnails__badge') !== null
              && !thumb.querySelector('.icon-3d-model');

            if (isVideo) {
              const videoDialogOpener = this.querySelector('side-drawer-opener[data-side-drawer^="#VideoModal"]');
              if (videoDialogOpener) {
                const button = videoDialogOpener.querySelector('button');
                if (button) button.click();
                return;
              }
            }

            if (mediaId) {
              this.setActiveMedia(mediaId);
            }
          });
        });
      }

      setActiveMedia(mediaId, smooth = false) {
        if (!mediaId) return false;

        let cleanMediaId = mediaId;
        if (mediaId.indexOf('-') !== -1) {
          const parts = mediaId.split('-');
          cleanMediaId = parts[parts.length - 1];
        }

        let element = this.querySelector(`.gallery-main__item[data-media-id="${cleanMediaId}"]`)
          || this.querySelector(`.gallery-main__item[data-media-id="${mediaId}"]`);

        if (!element) {
          element = this.querySelector(`[data-media-id="${cleanMediaId}"]`);
        }

        if (!element) {
          element = this.querySelector(`[data-media-id="${mediaId}"]`);
        }

        if (!element) return false;

        const galleryMain = this.querySelector('[data-gallery-main]');
        if (galleryMain) {
          galleryMain.querySelectorAll('.gallery-main__item.active').forEach((item) => item.classList.remove('active'));
        }

        this.querySelectorAll('[data-media-id].active').forEach((node) => {
          if (!node.classList.contains('gallery-main__item')) {
            node.classList.remove('active');
          }
        });

        element.classList.add('active');

        const allMainItems = galleryMain ? Array.from(galleryMain.querySelectorAll('.gallery-main__item')) : [];
        const mediaIndex = allMainItems.indexOf(element);

        this.syncSlideshow(element, mediaIndex, smooth);
        this.syncThumbnails(element, mediaIndex);

        if (this.classList.contains('media-gallery--grid')) {
          this.swapGridMedia(element, mediaIndex);
        }

        return true;
      }

      syncSlideshow(mediaElement, mediaIndex, smooth = false) {
        try {
          if (!this.classList.contains('media-gallery--carousel')) return;

          const galleryMain = this.querySelector('[data-gallery-main]');
          if (!galleryMain) return;

          const items = galleryMain.querySelectorAll('.gallery-main__item');
          items.forEach((item) => item.classList.remove('active'));

          let targetItem = null;
          if (mediaElement && mediaElement.classList.contains('gallery-main__item')) {
            targetItem = mediaElement;
          } else if (mediaIndex >= 0 && mediaIndex < items.length) {
            targetItem = items[mediaIndex];
          }

          if (targetItem) {
            targetItem.classList.add('active');

            const deferredMedia = targetItem.querySelector('deferred-media');
            if (deferredMedia && !deferredMedia.hasAttribute('data-media-loaded')) {
              deferredMedia.loadContent();
            }
          }
        } catch (e) {
          // silent
        }
      }

      syncThumbnails(mediaElement, mediaIndex) {
        try {
          const thumbnailsContainer = this.querySelector('.gallery-thumbnails');
          if (!thumbnailsContainer) return;

          const thumbnailButtons = thumbnailsContainer.querySelectorAll('.gallery-thumbnails__item');
          if (thumbnailButtons.length === 0) return;

          thumbnailButtons.forEach((btn) => btn.classList.remove('active'));

          let targetThumb = null;
          const mediaId = mediaElement ? mediaElement.getAttribute('data-media-id') : null;

          if (mediaId) {
            targetThumb = thumbnailsContainer.querySelector(`.gallery-thumbnails__item[data-media-id="${mediaId}"]`);
          }
          if (!targetThumb && mediaIndex >= 0) {
            targetThumb = thumbnailsContainer.querySelector(`.gallery-thumbnails__item[data-index="${mediaIndex}"]`);
          }

          if (targetThumb) {
            targetThumb.classList.add('active');
            const containerRect = thumbnailsContainer.getBoundingClientRect();
            const thumbRect = targetThumb.getBoundingClientRect();
            const isHorizontal = thumbnailsContainer.scrollWidth > thumbnailsContainer.clientHeight;

            if (isHorizontal) {
              const scrollLeft = thumbnailsContainer.scrollLeft;
              const thumbLeft = thumbRect.left - containerRect.left + scrollLeft;
              const thumbWidth = thumbRect.width;
              const containerWidth = containerRect.width;
              const targetScroll = thumbLeft - containerWidth / 2 + thumbWidth / 2;
              thumbnailsContainer.scrollTo({ left: Math.max(0, targetScroll), behavior: 'smooth' });
            } else {
              const scrollTop = thumbnailsContainer.scrollTop;
              const thumbTop = thumbRect.top - containerRect.top + scrollTop;
              const thumbHeight = thumbRect.height;
              const containerHeight = containerRect.height;
              const targetScroll = thumbTop - containerHeight / 2 + thumbHeight / 2;
              thumbnailsContainer.scrollTo({ top: Math.max(0, targetScroll), behavior: 'smooth' });
            }
          }
        } catch (e) {
          // silent
        }
      }

      swapGridMedia() {
        try {
          if (typeof MainEvents !== 'undefined' && MainEvents.variantUpdate) {
            const handleVariantUpdateOnce = (event) => {
              try {
                const source = event?.detail?.data?.html;
                if (!source) return;

                const newMediaGallery = source.querySelector('media-gallery');
                if (!newMediaGallery) return;
                this.replaceWith(newMediaGallery);

                try {
                  newMediaGallery.scrollIntoView({ behavior: 'smooth' });
                } catch (e) {
                  // ignore scrolling errors
                }
              } catch (e) {
                // silent
              } finally {
                document.removeEventListener(MainEvents.variantUpdate, handleVariantUpdateOnce);
              }
            };
            document.addEventListener(MainEvents.variantUpdate, handleVariantUpdateOnce);
          }

        } catch (e) {
          // silent
        }
      }
    }
  );
}
